import React, { memo, useRef } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { Modal } from 'Components/Modal';
import { Spinner } from 'Components/Spinner';
import { PurpleButton } from 'Components/Button';

import classes from './uploadFileModal.module.css';

interface Props {
  isOpen: boolean;
  fetching: boolean;
  file: File | null;
  dragActive: boolean;
  modalCloseClick: (e: any) => void;
  uploadButtonClick: (e: any) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
}

function UploadFileModal({
  isOpen,
  fetching,
  file,
  dragActive,
  modalCloseClick,
  uploadButtonClick,
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dropzoneClass = [
    classes.dropzone,
    dragActive ? classes.dropzoneActive : '',
    file ? classes.dropzoneFile : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      id="pdf-upload-modal"
      classes={classes}
      title="Upload PDF"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={modalCloseClick}
    >
      {fetching ? (
        <div className={classes.uploadingBox}>
          <Spinner loading />
          <p style={{ marginTop: 12 }}>Uploading...</p>
        </div>
      ) : (
        <>
          <div
            className={dropzoneClass}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
          >
            {file ? (
              <span className={classes.fileName}>{file.name}</span>
            ) : (
              <span>Drop a PDF file here</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={onFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className={classes.browseBtn}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? 'Choose a Different File' : 'Browse Files'}
          </button>
        </>
      )}
      <div className={classes.buttonWrapper}>
        <PurpleButton onClick={uploadButtonClick} disabled={fetching || !file}>
          Upload
        </PurpleButton>
      </div>
    </Modal>
  );
}

const UploadFileModalMemo = memo(UploadFileModal, areEqual);

export { UploadFileModalMemo as UploadFileModal };
